import React, { type ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { Ionicons } from './icons';
import { styleMiroir } from '../i18n/sens';
import { colors, formes, shadow, spacing, typography } from '../theme/tokens';

export interface LigneActionProps {
  titre: string;
  sousTitre?: string;
  icone?: ReactNode;
  // Remplace le chevron final (une note, un compteur, un menu).
  accessoire?: ReactNode;
  // Ce que dit l'accessoire, pour le lecteur d'écran : le bouton se lit d'un trait et masque ses
  // enfants, un score posé en accessoire ne s'annoncerait jamais sans cette phrase.
  detailAccessible?: string;
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
}

/**
 * Ligne de carte cliquable : icône, titre, sous-titre, chevron. Elle dédoublonne `EntryCard` et
 * `ActionRow`, copiés à l'identique dans les écrans de CV et de lettres. Les écrans actuels ne sont
 * pas migrés ici : chacun passe à `LigneAction` dans le lot de sa refonte.
 */
export function LigneAction({
  titre,
  sousTitre,
  icone,
  accessoire,
  detailAccessible,
  onPress,
  disabled = false,
  testID,
}: LigneActionProps) {
  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={[titre, sousTitre, detailAccessible].filter(Boolean).join(', ')}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[styles.ligne, disabled && styles.inactive]}
    >
      {icone ? <View style={styles.icone}>{icone}</View> : null}
      <View style={styles.textes}>
        <Text style={styles.titre}>{titre}</Text>
        {sousTitre ? <Text style={styles.sousTitre}>{sousTitre}</Text> : null}
      </View>
      {accessoire ?? (
        <View testID={testID ? `${testID}-chevron` : undefined} style={styleMiroir()}>
          <Ionicons name="chevron-forward" size={18} color={colors.grey400} />
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  ligne: {
    minHeight: formes.hauteur.lg,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: formes.rayon.md,
    backgroundColor: colors.white,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    ...shadow.card,
  },
  inactive: { opacity: 0.5 },
  icone: { alignItems: 'center', justifyContent: 'center' },
  // `minWidth: 0` : sans lui, un titre long ne se coupe pas et le chevron sort de la carte.
  textes: { flex: 1, minWidth: 0, gap: 2 },
  titre: {
    fontSize: typography.bodyMd.size,
    lineHeight: typography.bodyMd.lineHeight,
    fontWeight: '800',
    color: colors.night,
  },
  sousTitre: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
});
